<template>
  <div class="content">
    <!-- Nagłówek sekcji czarnej listy -->
    <h3 class="center-aligned">{{ t('black-list').toUpperCase() }}:</h3>

    <!-- Formularz dodawania pojedynczego pojazdu do czarnej listy -->
    <div class="form center-aligned">
      <div class="form-group">
        <label for="licensePlate" class="label-license-plate">{{ t('plate-no') }}:</label>
        <!-- Pole na numer rejestracyjny -->
        <input id="licensePlate" v-model="licensePlate" type="text" />
      </div>
      <div class="form-group">
        <label for="description" class="label-description">{{ t('vehicle-description') }}:</label>
        <!-- Opis pojazdu -->
        <input id="description" v-model="description" type="text" maxlength="100" />
      </div>
      <!-- Wybór użytkownika (dostępne tylko dla osób z uprawnieniami UserManagement) -->
      <div v-if="hasAccess('UserManagement')" class="form-group">
        <label for="user" class="label-user">Przypisz użytkownika:</label>
        <select id="user" v-model="selectedUserId">
          <option value="">Przypisz użytkownika</option>
          <option v-for="user in users"
                  :key="user.id"
                  :value="user.id">
            {{ user.username }}
          </option>
        </select>
      </div>
      <!-- Dodawanie pojazdu, aktywne jeśli oba pola są uzupełnione -->
      <button @click="addPlate" :disabled="!licensePlate || !description" class="button-save">{{ t('add') }}</button>
      <div>
        {{ t('or') }}
      </div>
      <!-- Sekcja importu pojazdów z pliku CSV -->
      <div class="file-upload center-aligned">
        <input type="file" @change="handleFileUpload" accept=".csv" ref="fileInput"/>
        <div>
          <!-- Wyświetlanie nazwy pliku i możliwość usunięcia -->
          <span v-if="uploadedFileName">{{ uploadedFileName }} <button @click="removeFile" class="button-delete">X</button></span>
        </div>
        <!-- Podgląd pojazdów zaimportowanych z CSV przed dodaniem -->
        <div v-if="csvVehicles.length > 0">
          <h4>{{ t('ready-to-send-csv-vehicles') }}:</h4>
          <table class="csv-vehicle-table center-aligned">
            <thead>
              <tr>
                <th>{{ t('ordinal-number') }}</th>
                <th>{{ t('short-plate-no') }}</th>
                <th v-if="hasAccess('UserManagement')">Przypisany użytkownik</th>
                <th>{{ t('vehicle-description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- Wiersz dla każdego pojazdu z CSV -->
              <tr v-for="(vehicle, index) in csvVehicles" :key="vehicle.id">
                <td>{{ index + 1 }}</td>
                <td>{{ vehicle.licensePlate }}</td>
                <td v-if="hasAccess('UserManagement')">
                  <!-- Możliwość przypisania użytkownika dla pojazdu z CSV -->
                  <select :id="`user-${vehicle.id}`" v-model="vehicle.userId">
                    <option value="">Przypisz użytkownika</option>
                    <option v-for="user in users"
                            :key="user.id"
                            :value="user.id">
                      {{ user.username }}
                    </option>
                  </select>
                </td>
                <td>{{ vehicle.description }}</td>
                <td><button @click="removeCSVVehicle(index)" class="button-delete">{{ t('delete') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Przycisk do masowego dodania pojazdów z CSV -->
      <button v-if="csvVehicles.length > 0" @click="submitCSVVehicles" class="button-save">{{ t('add-vehicles-from-csv') }}</button>
    </div>

    <!-- Wyświetlenie istniejących pojazdów na czarnej liście -->
    <div v-if="vehicles.length > 0">
      <table class="vehicle-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>{{ t('short-plate-no') }}</th>
            <th>Przypisany użytkownik</th>
            <th>{{ t('hits-no') }}</th>
            <th>{{ t('monitored-since') }}</th>
            <th>{{ t('vehicle-description') }}</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <!-- Wiersz dla każdego pojazdu -->
          <tr v-for="(vehicle, index) in paginatedVehicles" :key="vehicle.id">
            <td>{{ index + 1 }}</td>
            <td>{{ vehicle.licensePlate }}</td>
            <td>{{ vehicle.username }}</td>
            <td>{{ vehicle.hits }}</td>
            <td>{{ formatDateTime(vehicle.monitoredSince) }}</td>
            <td>{{ vehicle.description }}</td>
            <td><button @click="removeVehicle(vehicle.id)" class="button-delete">{{ t('delete') }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Jeśli nie ma pojazdów na liście -->
    <div v-else>
      <p class="center-aligned">{{ t('no-vehicles-on-black-list') }}</p>
    </div>
  </div>
</template>

<script>
import BlackListsService from "../services/BlackListsService"; // Serwis API czarnej listy
import Papa from 'papaparse'; // Do parsowania CSV
import { useUserStore } from "@/stores/user.store";

export default {
  name: 'BlackList',
  data() {
    return {
      licensePlate: '',    // Pole formularza: numer rejestracyjny
      description: '',     // Pole formularza: opis
      vehicles: [],        // Lista pojazdów z bazy (czarna lista)
      csvVehicles: [],     // Pojazdy przygotowane do importu z CSV
      uploadedFileName: '',// Nazwa załadowanego pliku CSV
      currentPage: 1,      // Numer bieżącej strony w paginacji
      pageSize: 5,         // Ilość rekordów na stronę
      users: [],           // Lista użytkowników do przypisania
      selectedUserId: null,// Wybrany użytkownik w formularzu
      username: ''         // Nazwa użytkownika (jeśli przypisany)
    };
  },
  computed: {
    // Wylicza pojazdy do wyświetlenia na bieżącej stronie
    paginatedVehicles() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = this.currentPage * this.pageSize;
      return this.vehicles.slice(start, end);
    },
    // Ilość wszystkich stron
    totalPages() {
      return Math.ceil(this.vehicles.length / this.pageSize);
    }
  },
  methods: {
    // Sprawdza uprawnienia użytkownika
    hasAccess(id) {
      const userStore = useUserStore();
      return userStore.hasAccess(id);
    },
    // Pobiera użytkowników z API (do przypisywania)
    async getUsers() {
      try {
        const response = await BlackListsService.getUsers();
        this.users = response.map(u => ({
          id:       u.id,
          username: u.username
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    // Dodaje pojedynczy pojazd z formularza do czarnej listy
    async addPlate() {
      try {
        const response = await BlackListsService.postNewPlate(
          this.licensePlate,
          this.description,
          this.selectedUserId
        );
        this.vehicles.push({
          licensePlate: this.licensePlate,
          description: this.description,
          hits: 0,
          monitoredSince: new Date().toISOString(),
          id: response.id,
          username: response.user.username
        });
        this.licensePlate = '';
        this.description = '';
        this.selectedUserId = null;
      } catch (error) {
        if (error.message === 'Both license plate and description are required.') {
          console.warn(error.message);
        } else {
          console.error('Error adding vehicle:', error);
        }
      }
    },
    // Pobiera aktualną listę pojazdów z API (czarna lista)
    async fetchVehicles() {
      try {
        const response = await BlackListsService.getAll();
        this.vehicles = response.map(vehicle => ({
          licensePlate: vehicle.plateLpr,
          description: vehicle.description,
          hits: vehicle.hitCounter ?? 0,
          monitoredSince: vehicle.createdAt || new Date().toISOString(),
          id: vehicle.id,
          userId: vehicle.user.id,
          username: vehicle.user.username
        }));
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    },
    // Usuwa pojazd po id z czarnej listy
    async removeVehicle(id) {
      try {
        await BlackListsService.delete(id);
        this.vehicles = this.vehicles.filter(vehicle => vehicle.id !== id);
      } catch (error) {
        console.error('Error deleting vehicle:', error);
      }
    },
    // Przejście na poprzednią stronę paginacji
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    // Przejście na następną stronę paginacji
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    // Formatowanie daty do czytelnego formatu
    formatDateTime(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    },
    // Obsługa uploadu pliku CSV
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name;
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          delimiter: ';',
          complete: this.addVehiclesFromCSV,
          error: function (error) {
            console.error('Error reading CSV file:', error);
          }
        });
      }
    },
    // Parsuje dane z CSV i przygotowuje do wysłania
    addVehiclesFromCSV(results) {
      this.csvVehicles = results.data.map(vehicle => ({
        licensePlate: vehicle[0],
        description: vehicle[1],
        hits: 0,
        monitoredSince: new Date().toISOString(),
        id: Date.now() + Math.random(), // Unikalne id tymczasowe
        userId: null,
      }));
    },
    // Dodaje wszystkie pojazdy z CSV do czarnej listy
    async submitCSVVehicles() {
      for (let vehicle of this.csvVehicles) {
        try {
          const response = await BlackListsService.postNewPlate(
            vehicle.licensePlate,
            vehicle.description,
            vehicle.userId
          );
          this.vehicles.push({
            licensePlate: vehicle.licensePlate,
            description: vehicle.description,
            hits: 0,
            monitoredSince: vehicle.createdAt || new Date().toISOString(),
            id: response.id,
            username: response.user.username,
          });
        } catch (error) {
          console.error('Error adding vehicle from CSV:', error);
        }
      }
      // Czyszczenie po imporcie
      this.csvVehicles = [];
      this.uploadedFileName = '';
      this.$refs.fileInput.value = '';
    },
    // Usuwa plik CSV i przygotowaną listę do importu
    removeFile() {
      this.uploadedFileName = '';
      this.csvVehicles = [];
      this.$refs.fileInput.value = '';
    },
    // Usuwa pojedynczy pojazd z listy CSV (jeszcze nie dodany)
    removeCSVVehicle(index) {
      this.csvVehicles.splice(index, 1);
    },
  },
  // Po zamontowaniu pobiera użytkowników i pojazdy
  mounted() {
    this.getUsers();
    this.fetchVehicles();
  }
};
</script>

<style scoped>
h3 {
  padding: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #aeaeae;
}

/* .form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}
*/
.form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10rem;
}

.form .form-group .label-license-plate {
  padding-right: 0.6rem;
}

.form .form-group .label-description {
  padding-right: 3.87rem;
}

.form .form-group .label-user {
  padding-right: 0.1rem;
}

.form .form-group select {
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
}
/*
.form button {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
} */

.vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.vehicle-table th,
.vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.vehicle-table td button {
  /* width: 75%; */
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* .pagination button {
  padding: 5px 10px;
  margin: 0 5px;
} */

p {
  font-size: large;
}

.file-upload {
  margin: 20px 0;
}

.file-upload span {
  margin-left: 10px;
}

/* .file-upload button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
} */

.csv-vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.csv-vehicle-table th,
.csv-vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

/* .csv-vehicle-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
} */

.button-delete {
  padding: 0.25rem 1rem 0.25rem 1rem;
}
</style>

